import {
    addFrontendPropertiesToSingle,
    addFrontendPropertiesToTentatives,
    addIndexesAsOrderToTentatives,
} from './addFrontendProperties.js';
import {
    createTentativePackage,
    createTentativeSection,
    createTentativeService,
    detachAllServicesRelatedTo,
} from './createTentatives.js';
import { tentativeSectionsFromTentatives, tentativeServicesFromTentatives } from './tentativeHelpers.js';
import { updateBaseData, updatePackageBaseData } from './updateBaseData.js';
import { modifyPriceBasedOnPriceType } from '../../../../../mixins/PriceMethods.js';
import { daysBetweenDateTimes } from '../../../../../mixins/DateHelpersMixin.js';

export const tentatives = {
    namespaced: true,
    state: {
        modifiedTentatives: {},
        modifiedTentativeServices: [],
        modifiedTentativeConfig: [],
        prices: null,
        deletedTentatives: {
            services: [],
            sections: [],
            rates: [],
            bookings: [],
            packages: [],
        },
    },
    actions: {
        deleteSection(context, payload) {
            payload.tentativeSection.tentativeServices.forEach((tentativeService) => {
                context.commit('deleteService', {
                    tentativeSection: payload.tentativeSection,
                    tentativeService: tentativeService,
                });
            });
            context.commit('deleteSection', payload);
        },
        deleteService(context, payload) {
            context.commit('deleteService', payload);
            if (payload.tentativeService.rate) {
                context.commit('deleteRate', payload.tentativeService.rate);
            }
            if (payload.tentativeService.booking) {
                context.commit('deleteBooking', payload.tentativeService.booking);
            }
        },
        deletePackage(context, tentativePackage) {
            context.commit('deletePackage', tentativePackage);
            tentativePackage.tentativeSections.forEach((tentativeSection) => {
                context.commit('deleteSection', {
                    tentativeSection: tentativeSection,
                    tentativePackage: tentativePackage,
                });
                tentativeSection.tentativeServices.forEach((tentativeService) => {
                    context.commit('deleteService', {
                        tentativeSection: tentativeSection,
                        tentativeService: tentativeService,
                    });
                });
            });
        },
    },
    mutations: {
        // Set
        setTentativeConfig(state, tentativeConfig) {
            state.modifiedTentativeConfig = tentativeConfig;
        },
        setTentatives(state, tentatives) {
            state.modifiedTentatives = addFrontendPropertiesToTentatives(tentatives, state.modifiedTentativeConfig);
        },
        setPrices(state, prices) {
            state.prices = prices;
        },

        // Modify prices based on price types
        modifyPricesOnTentatives(state) {
            tentativeSectionsFromTentatives(state.modifiedTentatives).forEach((tentativeSection) => {
                tentativeSection.tentativeServices.forEach((tentativeService) => {
                    if (tentativeService.line_type !== 'available') {
                        tentativeService.price = modifyPriceBasedOnPriceType(
                            tentativeService.price,
                            tentativeService.price_type,
                            daysBetweenDateTimes(tentativeSection.start_date, tentativeSection.end_date) + 1
                        );
                        tentativeService.price_vat = modifyPriceBasedOnPriceType(
                            tentativeService.price_vat,
                            tentativeService.price_type,
                            daysBetweenDateTimes(tentativeSection.start_date, tentativeSection.end_date) + 1
                        );
                    }
                });
            });
        },

        // Create
        createPackage(state, payload) {
            createTentativePackage(state, payload.event);
        },
        createSection(state, payload) {
            createTentativeSection(state, payload.lineType, payload.event, payload.tentativePackage);
        },
        createService(state, payload) {
            createTentativeService(state, payload.lineType, payload.tentativeSection, payload.event);
        },

        // Delete
        deletePackage(state, tentativePackage) {
            state.deletedTentatives.packages.push(tentativePackage);
            state.modifiedTentatives['packages'].splice(
                state.modifiedTentatives['packages'].indexOf(tentativePackage),
                1
            );
        },
        deleteSection(state, payload) {
            state.deletedTentatives.sections.push(payload.tentativeSection);
            if (payload.tentativePackage) {
                payload.tentativePackage.tentativeSections.splice(
                    payload.tentativePackage.tentativeSections.indexOf(payload.tentativeSection),
                    1
                );
            } else {
                state.modifiedTentatives[payload.lineType].splice(
                    state.modifiedTentatives[payload.lineType].indexOf(payload.tentativeSection),
                    1
                );
            }
        },
        deleteService(state, payload) {
            detachAllServicesRelatedTo(state, payload);
            state.deletedTentatives.services.push(payload.tentativeService);
            const index = payload.tentativeSection.tentativeServices.indexOf(payload.tentativeService);
            if (index !== -1) {
                payload.tentativeSection.tentativeServices.splice(index, 1);
            }
        },
        deleteRate(state, tentativeService) {
            state.deletedTentatives.rates.push(tentativeService.rate);
        },
        deleteBooking(state, tentativeService) {
            state.deletedTentatives.bookings.push(tentativeService.booking);
        },

        swapService(state, payload) {
            // Remove the service from the section
            const index = payload.tentativeSection.tentativeServices.indexOf(payload.tentativeService);
            state.deletedTentatives.services.push(payload.tentativeService);
            payload.tentativeSection.tentativeServices.splice(index, 1);

            // Add the new service to the section
            let swapWith = {
                ...payload.swapWith,
            };

            addFrontendPropertiesToSingle(swapWith);
            swapWith.external_id = payload.tentativeService.external_id;
            swapWith.parent_external_id = payload.tentativeService.parent_external_id;
            swapWith.random_key = payload.tentativeService.random_key;
            swapWith.package_grouping_id = payload.tentativeService.package_grouping_id;
            swapWith.quantity = payload.tentativeService.quantity;
            swapWith.line_type = payload.tentativeService.line_type;
            swapWith.price_vat = payload.tentativeService.price_vat;
            swapWith.price = payload.tentativeService.price;
            swapWith.vat_percentage = payload.tentativeService.vat_percentage;
            payload.tentativeSection.tentativeServices.splice(index, 0, swapWith);

            // Re-attach services related to the detached service
            payload.swapWith = swapWith;
        },

        attachServiceToService(state, payload) {
            payload.tentativeService.parent_random_key = payload.attachTo.random_key;
            payload.tentativeService.parentTentativeService = payload.attachTo;
        },
        attachServicesToService(state, payload) {
            payload.tentativeSection.tentativeServices.forEach((tentativeService) => {
                tentativeService.parent_random_key = payload.attachTo.random_key;
                tentativeService.parentTentativeService = payload.attachTo;
            });
        },

        // Other
        setOrderOnTentatives(state) {
            state.modifiedTentatives = addIndexesAsOrderToTentatives(
                state.modifiedTentatives,
                state.modifiedTentativeConfig
            );
        },
        modifyPackageBaseData(state, payload) {
            updatePackageBaseData(state.modifiedTentatives, state.modifiedTentativeConfig, payload);
        },
        modifyParent(state, modifiedParent) {
            updateBaseData(state, modifiedParent);
        },
    },
    getters: {
        lineTypes(state) {
            if (state.modifiedTentativeConfig.type === 'legacy') {
                return ['legacy'];
            } else if (state.modifiedTentativeConfig.type === 'package') {
                return ['package', 'selected', 'available'];
            } else {
                // Hide package lines if empty
                if (
                    state.modifiedTentatives.package === undefined ||
                    (state.modifiedTentatives.package && state.modifiedTentatives.package.length === 0)
                ) {
                    return ['selected', 'available'];
                } else {
                    return ['package', 'selected', 'available'];
                }
            }
        },
        tentatives(state) {
            return state.modifiedTentatives;
        },
        tentativeConfig(state) {
            return state.modifiedTentativeConfig;
        },
        deletedTentatives(state) {
            return state.deletedTentatives;
        },
        tentativeServices(state) {
            return tentativeServicesFromTentatives(state.modifiedTentatives);
        },
        tentativePrices(state) {
            return state.prices;
        },
    },
};
